import { Order } from "../types";
import { runtimeConfig } from "../config/runtimeConfig";
import { authService } from "./auth.service";
import { buildAppPath } from "../config/appBasePath";

// Single API base URL — all calls go through the unified backend.
export const API_BASE_URL = runtimeConfig.adminApiUrl;

/**
 * DRY fetch wrapper that automatically attaches the JWT Bearer token.
 * If a 401 is received the session is cleared and the user is redirected to login
 * (the new backend does not support refresh tokens).
 */
async function fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
    const headers = new Headers(options.headers || {});

    const token = authService.getAccessToken();
    if (token) {
        headers.set("Authorization", `Bearer ${token}`);
    }

    if (!headers.has("Content-Type")) {
        headers.set("Content-Type", "application/json");
    }

    const config: RequestInit = {
        ...options,
        headers,
        credentials: "include"
    };

    const response = await fetch(url, config);

    // If 401, token has expired — redirect to login
    if (response.status === 401 && token) {
        authService.clearSession();
        window.location.href = buildAppPath("/login");
    }

    return response;
}

/**
 * POST helper — all new-backend actions use POST /Controller/Action.
 */
async function postAction<T = any>(action: string, body: Record<string, any> = {}): Promise<T> {
    const response = await fetchWithAuth(`${API_BASE_URL}/${action}`, {
        method: "POST",
        body: JSON.stringify(body),
    });
    if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || `Request failed: ${action}`);
    }
    return response.json();
}

// ─── Orders ────────────────────────────────────────────────
export const ordersApi = {
    getOrders: async (): Promise<Order[]> => {
        return postAction<Order[]>("Order/GetAll");
    },

    updateOrderStatus: async (id: number, status: string): Promise<void> => {
        await postAction("Order/UpdateStatus", { id, status });
    },

    getOrderById: async (id: number): Promise<Order> => {
        const response = await fetchWithAuth(`${API_BASE_URL}/Order/GetById`, {
            method: "POST",
            body: JSON.stringify(id),
        });
        if (!response.ok) throw new Error("Failed to fetch order");
        return response.json();
    },

    getFormSchema: async (type: string): Promise<any> => {
        return postAction("Admin/GetFormSchema", { type });
    },

    getOrderLogs: async (id: number): Promise<any[]> => {
        return postAction<any[]>("Admin/GetOrderLogs", { id });
    },
};

// ─── User Management ──────────────────────────────────────
export const userApi = {
    createClient: (data: any) => postAction("Admin/CreateClient", data),
    createSupplier: (data: any) => postAction("Admin/CreateSupplier", data),
    getAllUsers: () => postAction("User/GetAll"),
    addUser: (data: any) => postAction("User/Add", data),
    updateUser: (data: any) => postAction("User/Update", data),
    deleteUser: (data: any) => postAction("User/Delete", data),
};

// ─── Entity Management (Suppliers & Clients) ─────────────
export const entityApi = {
    getAllSuppliers: () => postAction("Supplier/GetAll"),
    createSupplier: (data: any) => postAction("Supplier/Add", data),
    updateSupplier: (data: any) => postAction("Supplier/Update", data),
    deleteSupplier: (data: any) => postAction("Supplier/Delete", data),
    getAllClients: () => postAction("Client/GetAll"),
    createClient: (data: any) => postAction("Client/Add", data),
    updateClient: (data: any) => postAction("Client/Update", data),
    deleteClient: (data: any) => postAction("Client/Delete", data),
};
